import { useTheme } from '@chakra-ui/react'
import { CheckIcon } from '@verbit-ai/icons-library'

import { DEFAULT_FONT_FAMILY_BY_LANGUAGE } from 'src/themes/types'
import { useSetThemeField } from 'src/contexts/ThemeContext'
import { useResponsiveContainer } from 'src/contexts/ResponsiveContainerContext'
import { FONT_FAMILIES_PRESENTATION } from 'src/components/ThemeToggle/types'
import { useLanguage } from 'src/contexts/LanguageContext'

import * as S from './styles'

type ColorRadioGroupSectionProps = {
    title: string
    groupId: string
    name: string
    colors: readonly { colorValue: string; colorTitle: string }[]
    activeIndex: number
    onSelect: (index: number) => void
    getStroke?: (colorValue: string) => boolean
}

const ColorRadioGroupSection = ({ title, groupId, name, colors, activeIndex, onSelect, getStroke }: ColorRadioGroupSectionProps) => (
    <S.ColorRadioGroup role="radiogroup" aria-labelledby={groupId}>
        <S.SectionTitle as="legend" id={groupId}>
            {title}
        </S.SectionTitle>
        <S.ColorsSelection>
            {colors.map(({ colorValue, colorTitle }, index) => (
                <S.ColorOption key={colorTitle}>
                    <S.VisuallyHiddenRadio
                        type="radio"
                        name={name}
                        value={index}
                        checked={index === activeIndex}
                        onChange={() => onSelect(index)}
                    />
                    <S.ColorIndicator
                        $colorValue={colorValue}
                        $isActive={index === activeIndex}
                        $stroke={getStroke?.(colorValue) ?? false}
                    />
                    {colorTitle}
                </S.ColorOption>
            ))}
        </S.ColorsSelection>
    </S.ColorRadioGroup>
)

export const ThemeTextCustomization = () => {
    const {
        palette: {
            transcript: { textColors, textBackgroundColors },
            themeToggle: { iconColor, colorOptionsBgColor },
        },
        fontFamily: activeFontFamily,
        availableFontFamilies,
        textColorIndex,
        textBackgroundColorIndex,
    } = useTheme()
    const setThemeField = useSetThemeField()
    const { isMediumContainer } = useResponsiveContainer()

    const { requestedLanguageName } = useLanguage()
    const defaultFontFamily = DEFAULT_FONT_FAMILY_BY_LANGUAGE[requestedLanguageName]

    return (
        <S.Container>
            <ColorRadioGroupSection
                title="Text background color"
                groupId="text-background-color-title"
                name="text-background-color"
                colors={textBackgroundColors}
                activeIndex={textBackgroundColorIndex}
                onSelect={(index) => setThemeField('textBackgroundColorIndex', index)}
                getStroke={(colorValue) => colorValue.toLowerCase() === colorOptionsBgColor.toLowerCase()}
            />
            {isMediumContainer && <S.SectionTitle>Font type</S.SectionTitle>}
            <S.FontsSelection>
                {availableFontFamilies.map((fontFamily) => {
                    const isSelected = fontFamily === activeFontFamily

                    return (
                        <S.FontOption
                            key={fontFamily}
                            className={isSelected && isMediumContainer ? S.FONT_OPTION_ACTIVE_CLASSNAME : ''}
                            aria-pressed={isSelected}
                            onClick={() => setThemeField('fontFamily', fontFamily)}>
                            {FONT_FAMILIES_PRESENTATION[fontFamily]}
                            {fontFamily === defaultFontFamily && ' (default)'}
                            {isSelected && !isMediumContainer && <CheckIcon color={iconColor} aria-hidden />}
                        </S.FontOption>
                    )
                })}
            </S.FontsSelection>
            <ColorRadioGroupSection
                title="Text color"
                groupId="text-color-title"
                name="text-color"
                colors={textColors}
                activeIndex={textColorIndex}
                onSelect={(index) => setThemeField('textColorIndex', index)}
            />
        </S.Container>
    )
}
